import { useState } from "react";
import { Check, Undo2 } from "lucide-react";
import dayjs from "dayjs";

import { Button } from "../ui/button";
import { Textarea } from "../ui/input";
import ToastMsg from "../common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { APPRAISAL_AGREE_GOALS } from "../../constants/api";

import { brandHeadingText, brandMutedText, brandPanelBorder, brandPanelGradient } from "../../components/ui/statusStyles";
import { cn } from "../../utils/tailwindUtils";
/**
 * The employee's side of goal agreement.
 *
 * Only rendered for cycles that run a goal-setting window; everything else
 * keeps the original acknowledge-then-evaluate flow. The dispute path is the
 * point of the screen — goals you cannot push back on are assigned, not
 * agreed — so it asks for a reason rather than accepting a bare refusal.
 */
const GoalAgreementPanel = ({ appraisal, onUpdated }) => {
    const { update } = useApiMethods();
    const [reason, setReason] = useState("");
    const [showDispute, setShowDispute] = useState(false);
    const [saving, setSaving] = useState(false);

    const status = appraisal?.status;
    const awaiting = status === "pending_goal_agreement";
    const sentBack = status === "goals_sent_back";
    const agreed = status === "goals_agreed";

    if (!appraisal?.cycle_uses_goal_phase) return null;
    if (!awaiting && !sentBack && !agreed) return null;

    const submit = async (action) => {
        if (action === "dispute" && !reason.trim()) {
            ToastMsg("error", "Tell your manager what you would like changed");
            return;
        }
        setSaving(true);
        try {
            const res = await update(APPRAISAL_AGREE_GOALS, {
                id: appraisal.id,
                action,
                reason: reason.trim(),
            });
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Saved");
                setShowDispute(false);
                setReason("");
                onUpdated?.(res.data);
            } else {
                ToastMsg("error", res?.message || "Could not save");
            }
        } catch (_) {
            ToastMsg("error", "Could not save");
        } finally {
            setSaving(false);
        }
    };

    if (agreed) {
        return (
            <div className="rounded-md border border-teal-200 dark:border-teal-900/40 bg-teal-50 dark:bg-teal-900/20 p-3 sm:p-4">
                <p className="text-sm font-medium text-teal-900 dark:text-teal-200">
                    You have agreed these goals
                    {appraisal.goals_agreed_at
                        ? ` on ${dayjs(appraisal.goals_agreed_at).format("DD-MM-YYYY")}`
                        : ""}
                </p>
                <p className="text-xs text-teal-700 dark:text-teal-300 mt-0.5">
                    {appraisal.cycle_self_eval_start
                        ? `Your self-evaluation opens on ${dayjs(appraisal.cycle_self_eval_start).format("DD-MM-YYYY")}.`
                        : "Your self-evaluation will open when the cycle reaches that stage."}
                </p>
            </div>
        );
    }

    if (sentBack) {
        return (
            <div className="rounded-md border border-orange-200 dark:border-orange-900/40 bg-orange-50 dark:bg-orange-900/20 p-3 sm:p-4">
                <p className="text-sm font-medium text-orange-900 dark:text-orange-200">
                    Sent back to your manager
                </p>
                <p className="text-xs text-orange-700 dark:text-orange-300 mt-0.5">
                    You asked for: &ldquo;{appraisal.goals_dispute_reason}&rdquo;
                </p>
                <p className="text-xs text-orange-700 dark:text-orange-300 mt-1">
                    Your manager will revise the goals and send them back for agreement.
                </p>
            </div>
        );
    }

    return (
        <div className={cn("rounded-md border p-3 sm:p-4", brandPanelBorder, brandPanelGradient)}>
            <p className={cn("text-sm font-medium", brandHeadingText)}>
                Agree your goals for this cycle
            </p>
            <p className={cn("text-xs mt-0.5", brandMutedText)}>
                Read the KPIs and weightages below. You will be assessed against these at
                the end of the cycle, so if something looks wrong, say so now.
                {appraisal.cycle_goal_setting_end
                    ? ` Goal setting closes on ${dayjs(appraisal.cycle_goal_setting_end).format("DD-MM-YYYY")}.`
                    : ""}
            </p>

            {!showDispute ? (
                <div className="flex flex-wrap gap-2 mt-3">
                    <Button size="sm" variant="brand" disabled={saving} onClick={() => submit("agree")}>
                        <Check className="w-4 h-4 mr-1" /> Agree to these goals
                    </Button>
                    <Button size="sm" variant="outline" disabled={saving} onClick={() => setShowDispute(true)}>
                        <Undo2 className="w-4 h-4 mr-1" /> Request changes
                    </Button>
                </div>
            ) : (
                <div className="mt-3 space-y-2">
                    <Textarea
                        rows={3}
                        autoFocus
                        placeholder="What would you like changed? e.g. the weighting on patient feedback is too high given my shift pattern."
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                    />
                    <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="brand" disabled={saving || !reason.trim()}
                            onClick={() => submit("dispute")}>
                            Send back to manager
                        </Button>
                        <Button size="sm" variant="ghost" disabled={saving}
                            onClick={() => { setShowDispute(false); setReason(""); }}>
                            Cancel
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GoalAgreementPanel;
